import { useEffect, useRef } from 'react';
import { X } from 'lucide-react';

/**
 * Modal do painel.
 *
 * Usa o <dialog> nativo em vez de uma div com position:fixed. Isso entrega de
 * graça o que costuma ser reimplementado errado: foco preso dentro do diálogo,
 * fechar no Esc, e o resto da página marcado como inerte para leitores de tela.
 */

interface ModalProps {
  aberto: boolean;
  aoFechar: () => void;
  titulo: string;
  descricao?: string;
  children: React.ReactNode;
  rodape?: React.ReactNode;
  largura?: 'normal' | 'largo';
}

export function Modal({
  aberto,
  aoFechar,
  titulo,
  descricao,
  children,
  rodape,
  largura = 'normal',
}: ModalProps) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;

    if (aberto && !dialog.open) dialog.showModal();
    else if (!aberto && dialog.open) dialog.close();
  }, [aberto]);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;

    // O Esc do <dialog> fecha sozinho, sem passar pelo nosso estado. Sem
    // escutar o cancel, o React continuaria achando que o modal está aberto e
    // ele não reabriria no próximo clique.
    const aoCancelar = (e: Event) => {
      e.preventDefault();
      aoFechar();
    };
    dialog.addEventListener('cancel', aoCancelar);
    return () => dialog.removeEventListener('cancel', aoCancelar);
  }, [aoFechar]);

  // Sobre a estrutura abaixo:
  //
  // `m-auto` é obrigatório. O <dialog> nativo se centraliza sozinho com
  // `margin: auto`, e a preflight do Tailwind zera a margem de todos os
  // elementos — sem restaurá-la, o modal encosta no canto superior esquerdo.
  //
  // `hidden open:block`, e nunca um display puro. O navegador esconde o diálogo
  // fechado com `dialog:not([open]) { display: none }`, que é um seletor de
  // elemento — qualquer classe de display vence por especificidade e faz o
  // modal aparecer sozinho ao abrir a página. O `open:` restringe o display ao
  // estado aberto e devolve o controle ao navegador.
  //
  // O flex + max-height ficam num DIV interno, não no <dialog>. O Safari (macOS)
  // dimensiona errado um <dialog> que é ele próprio o container flex com
  // max-height: abre com altura mínima e o conteúdo rola dentro de uma faixa
  // pequena. Com o flex num div comum, o Safari calcula certo, e o <dialog> só
  // centraliza e desenha o backdrop. O corpo rola por dentro para cabeçalho e
  // rodapé ficarem sempre visíveis (o botão de confirmar não sai da tela).
  return (
    <dialog
      ref={ref}
      // O backdrop é estilizado no CSS global; clicar nele fecha, mas o clique
      // dentro do conteúdo não pode borbulhar para cá.
      onClick={(e) => {
        if (e.target === ref.current) aoFechar();
      }}
      className={`m-auto hidden w-[calc(100vw-2rem)] open:block overflow-visible border-0 bg-transparent p-0
        backdrop:bg-escuro-overlay/45 backdrop:backdrop-blur-[2px]
        ${largura === 'largo' ? 'max-w-[720px]' : 'max-w-[480px]'}`}
    >
      <div className="flex max-h-[calc(100dvh-3rem)] w-full flex-col overflow-hidden rounded-card bg-superficie shadow-modal">
        <div className="flex shrink-0 items-start gap-4 border-b border-borda px-6 py-5">
          <div className="min-w-0 flex-1">
            <h2 className="text-[16px] font-extrabold leading-tight">{titulo}</h2>
            {descricao && <p className="mt-1 text-[13px] text-texto-suave">{descricao}</p>}
          </div>
          <button
            onClick={aoFechar}
            aria-label="Fechar"
            className="-mr-1 grid size-8 shrink-0 place-items-center rounded-chip text-texto-mudo hover:bg-borda-suave hover:text-texto"
          >
            <X size={18} />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">{children}</div>

        {rodape && (
          <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-borda bg-borda-tenue px-6 py-4">
            {rodape}
          </div>
        )}
      </div>
    </dialog>
  );
}
